import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, searchUsersCallable, sendFriendRequestCallable, acceptFriendRequestCallable, declineFriendRequestCallable, removeFriendCallable } from '../firebase/config';
import { UserAddIcon, UsersIcon, SearchIcon } from '../components/Icons'; // Assuming you have these icons

const FriendsScreen = ({ user, onComposeClick }) => {
  const [activeTab, setActiveTab] = useState('myFriends'); // 'myFriends' or 'findFriends'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);

  // Combined listeners for friends and requests
  useEffect(() => {
    if (!user) return;

    const friendsRef = collection(db, 'users', user.uid, 'friends');
    const friendsUnsub = onSnapshot(friendsRef, (snapshot) => {
      const friendsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFriends(friendsList);
    });

    const requestsRef = collection(db, 'friend_requests');
    const incomingRequestsQuery = query(requestsRef, where('recipientId', '==', user.uid), where('status', '==', 'pending'));
    const incomingUnsub = onSnapshot(incomingRequestsQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFriendRequests(requests);
    });

    const outgoingRequestsQuery = query(requestsRef, where('senderId', '==', user.uid), where('status', '==', 'pending'));
    const outgoingUnsub = onSnapshot(outgoingRequestsQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOutgoingRequests(requests);
    });

    return () => {
      friendsUnsub();
      incomingUnsub();
      outgoingUnsub();
    };
  }, [user]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length === 0) return;

    setIsSearching(true);
    setSearchMessage('');
    setSearchResults([]);

    try {
      const result = await searchUsersCallable({ query: searchQuery });
      const foundUsers = result.data.users.filter(u => u.uid !== user.uid);
      setSearchResults(foundUsers);
      if (foundUsers.length === 0) {
        setSearchMessage('No users found matching your search.');
      }
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchMessage(error.message || 'An error occurred while searching.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendRequest = async (recipient) => {
    try {
      await sendFriendRequestCallable({ recipientId: recipient.uid, recipientUsername: recipient.username });
    } catch (error) {
      console.error("Error sending friend request:", error);
      alert(error.message);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await acceptFriendRequestCallable({ requestId });
    } catch (error) {
      console.error("Error accepting friend request:", error);
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await declineFriendRequestCallable({ requestId });
    } catch (error) {
      console.error("Error declining friend request:", error);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await declineFriendRequestCallable({ requestId }); // Same as declining
    } catch (error) {
      console.error("Error cancelling friend request:", error);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm("Are you sure you want to remove this friend?")) return;
    try {
      await removeFriendCallable({ friendId });
    } catch (error) {
      console.error("Error removing friend:", error);
    }
  };

  const getFriendshipStatus = (targetUid) => {
    if (friends.some(friend => friend.id === targetUid)) return 'friends';
    if (outgoingRequests.some(req => req.recipientId === targetUid)) return 'request_sent';
    if (friendRequests.some(req => req.senderId === targetUid)) return 'request_received';
    return 'none';
  };

  const renderMyFriends = () => (
    <div className="animate-fade-in">
      {/* Friend Requests */}
      {friendRequests.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Incoming Requests</h3>
          <div className="space-y-3">
            {friendRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
                <span className="font-semibold text-lg">@{req.senderUsername}</span>
                <div className="flex items-center space-x-3">
                  <button onClick={() => handleAcceptRequest(req.id)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">Accept</button>
                  <button onClick={() => handleDeclineRequest(req.id)} className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors">Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Your Friends ({friends.length})</h3>
        {friends.length > 0 ? (
          <div className="space-y-3">
            {friends.map(friend => (
              <div key={friend.id} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
                <span className="font-semibold text-lg">@{friend.username}</span>
                <div className="flex items-center space-x-3">
                  <button onClick={() => onComposeClick(`@${friend.username}`)} className="bg-teal-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors">Send Kiv</button>
                  <button onClick={() => handleRemoveFriend(friend.id)} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors">Remove</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">Your friends list is empty. Go to the "Find Friends" tab to add people!</p>
        )}
      </div>

      {/* Outgoing Requests */}
      {outgoingRequests.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4">Sent Requests</h3>
          <div className="space-y-3">
            {outgoingRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
                <span className="font-semibold text-lg">@{req.recipientUsername}</span>
                <button onClick={() => handleCancelRequest(req.id)} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors">Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderFindFriends = () => (
    <div className="animate-fade-in">
      <form onSubmit={handleSearch} className="flex items-center space-x-3 mb-6">
        <div className="relative flex-grow">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-3 pl-12 pr-4 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
          />
        </div>
        <button type="submit" disabled={isSearching} className="bg-[#D4AF37] text-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-600 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed">
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </form>

      {/* Search Results */}
      <div className="space-y-3">
        {searchResults.map(foundUser => {
          const status = getFriendshipStatus(foundUser.uid);
          const isFriend = status === 'friends';
          const requestSent = status === 'request_sent';
          const requestReceived = status === 'request_received';

          let button;
          if (isFriend) {
            button = <span className="text-green-500 font-semibold">Already Friends</span>;
          } else if (requestSent) {
            button = <span className="text-gray-400 font-semibold">Request Sent</span>;
          } else if (requestReceived) {
            button = <button onClick={() => handleAcceptRequest(friendRequests.find(r => r.senderId === foundUser.uid).id)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700">Accept Request</button>;
          } else {
            button = <button onClick={() => handleSendRequest(foundUser)} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700">Add Friend</button>;
          }

          return (
            <div key={foundUser.uid} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
              <span className="font-semibold text-lg">@{foundUser.username}</span>
              {button}
            </div>
          );
        })}
        {searchMessage && <p className="text-gray-400 text-center py-4">{searchMessage}</p>}
      </div>
    </div>
  );

  return (
    <div className="bg-[#1E1E1E]/50 p-6 sm:p-8 rounded-lg border border-gray-800 animate-fade-in">
      <div className="flex border-b border-gray-700 mb-6">
        <button onClick={() => setActiveTab('myFriends')} className={`flex items-center space-x-2 py-3 px-4 font-semibold text-lg transition-colors ${activeTab === 'myFriends' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-400 hover:text-white'}`}>
          <UsersIcon />
          <span>My Friends</span>
        </button>
        <button onClick={() => setActiveTab('findFriends')} className={`flex items-center space-x-2 py-3 px-4 font-semibold text-lg transition-colors ${activeTab === 'findFriends' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-400 hover:text-white'}`}>
          <UserAddIcon />
          <span>Find Friends</span>
        </button>
      </div>

      <div>
        {activeTab === 'myFriends' ? renderMyFriends() : renderFindFriends()}
      </div>
    </div>
  );
};

export default FriendsScreen;
